import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import sfx from './sfx';

/* ============================================================================
   Presentation settings — CRT intensity and sound.

   `fx` is written to <html data-fx="..."> and nothing else: tokens.css rewrites
   the --fx-* intensity variables per level, so every effect in the app dims or
   dies from that one attribute. No component needs to branch on the setting.
   ========================================================================== */

const STORAGE_KEY = 'decrypedia_settings';

export const FX_LEVELS = ['full', 'low', 'off'];

const DEFAULTS = {
  fx: 'full',
  // Off by default: activities run inside voice channels, where unrequested
  // audio is intrusive.
  sound: false,
};

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    return {
      fx: FX_LEVELS.includes(parsed.fx) ? parsed.fx : DEFAULTS.fx,
      sound: typeof parsed.sound === 'boolean' ? parsed.sound : DEFAULTS.sound,
    };
  } catch {
    return DEFAULTS;
  }
}

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(readStored);

  useEffect(() => {
    document.documentElement.setAttribute('data-fx', settings.fx);
    sfx.setEnabled(settings.sound);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* Private-mode storage failures shouldn't break the game. */
    }
  }, [settings]);

  const cycleFx = useCallback(() => {
    setSettings((prev) => {
      const next = FX_LEVELS[(FX_LEVELS.indexOf(prev.fx) + 1) % FX_LEVELS.length];
      return { ...prev, fx: next };
    });
  }, []);

  const toggleSound = useCallback(() => {
    setSettings((prev) => {
      const sound = !prev.sound;
      // Enable before the click's gesture context is gone so the AudioContext
      // can resume, then confirm audibly that it worked.
      sfx.setEnabled(sound);
      if (sound) sfx.play('click');
      return { ...prev, sound };
    });
  }, []);

  const value = useMemo(
    () => ({ ...settings, cycleFx, toggleSound }),
    [settings, cycleFx, toggleSound]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used inside a <SettingsProvider>');
  return ctx;
}

/** Convenience wrapper so components can fire sounds without importing sfx. */
export function useSfx() {
  return sfx;
}
